import Papa from 'papaparse';
import { extractAddress, isValidEmail } from './csv';

/**
 * Bulk input for the suppression list: either a pasted list of addresses or a
 * CSV such as a ZeroBounce / NeverBounce results export. When the file has a
 * verification status column, each address keeps its status so staff can
 * choose which results to block.
 */

const EMAIL_HEADERS = ['email', 'e mail', 'email address', 'emailaddress', 'mail', 'to'];

/** Status columns written by the common verification services. */
const STATUS_HEADERS = [
  'zb status',
  'status',
  'result',
  'verification status',
  'verification result',
  'email status',
  'state',
];

/** Statuses that mean the address is good — everything else is blocked by default. */
const DELIVERABLE_STATUS = /^(valid|deliverable|ok|safe|good)$/i;

export interface SuppressionCandidate {
  email: string;
  /** Verification result from the file, or '' when there is none. */
  status: string;
}

export interface SuppressionInput {
  candidates: SuppressionCandidate[];
  /** Entries that looked like addresses but aren't valid ones. */
  invalid: string[];
  /** How many repeats of an earlier address were dropped. */
  duplicates: number;
  /** The status column's original heading, or null if the input had none. */
  statusColumn: string | null;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, ' ');
}

export function isDeliverableStatus(status: string): boolean {
  return DELIVERABLE_STATUS.test(status.trim());
}

/** A first line with an email-like heading means the input is a CSV with headers. */
function findHeaderColumns(text: string): { email: string; status: string | null } | null {
  const firstLine = text.split(/\r?\n/).find((line) => line.trim()) ?? '';
  if (firstLine.includes('@')) return null;

  const headers = Papa.parse<string[]>(firstLine).data[0] ?? [];
  const email = headers.find((header) => {
    const normalized = normalizeHeader(header);
    return EMAIL_HEADERS.includes(normalized) || normalized.includes('email');
  });
  if (!email) return null;

  const status =
    STATUS_HEADERS.map((wanted) => headers.find((header) => normalizeHeader(header) === wanted))
      .find(Boolean) ?? null;
  return { email, status };
}

function collect(entries: Array<{ raw: string; status: string }>): Omit<SuppressionInput, 'statusColumn'> {
  const candidates: SuppressionCandidate[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  let duplicates = 0;

  for (const { raw, status } of entries) {
    const email = extractAddress(raw).replace(/^["']|["']$/g, '').toLowerCase();
    if (!email) continue;
    if (!isValidEmail(email)) {
      invalid.push(raw.trim());
      continue;
    }
    if (seen.has(email)) {
      duplicates += 1;
      continue;
    }
    seen.add(email);
    candidates.push({ email, status: status.trim() });
  }

  return { candidates, invalid, duplicates };
}

export function parseSuppressionInput(text: string): SuppressionInput {
  const columns = findHeaderColumns(text);

  if (columns) {
    const parsed = Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: 'greedy',
    });
    const entries = parsed.data.map((record) => ({
      raw: record[columns.email] ?? '',
      status: columns.status ? (record[columns.status] ?? '') : '',
    }));
    return { ...collect(entries), statusColumn: columns.status };
  }

  // Free text: one address per line, or separated by commas, semicolons, or
  // spaces. Words without an @ are display names and are ignored.
  const tokens = text.split(/[\s,;]+/).filter((token) => token.includes('@'));
  return { ...collect(tokens.map((raw) => ({ raw, status: '' }))), statusColumn: null };
}
